import React, { useEffect, useState } from 'react';
import { fmtList } from "@/lib/fmtList";
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Lock, Play, X } from 'lucide-react';
import { usePlatformAuth } from '@/contexts/PlatformAuthContext';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';
import { SButton } from '@/components/common/SButton';
import { trackClick } from '@/lib/track';
import { TipWidget } from '@/components/tips/TipWidget';
import { useJoinPrice } from '@/hooks/useJoinPrice';
import { CopyWithCoinPrice, COIN_PRICE_SLOT } from '@/components/common/CoinPrice';
import { TileImage } from '@/components/common/Skeleton';
import { useTranslation } from 'react-i18next';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Section 6 (01-home.md): a featured track on top + a locked catalog grid below.
// Featured track is excluded from the grid to avoid duplication (spec Open Q4).
// Catalog grid is hidden for subscribers - they have the real catalog.
const HomeMusic = () => {
  const { t } = useTranslation();
  const { everSubscribed } = usePlatformAuth();
  const { joinCoins } = useJoinPrice();
  const { playTrack } = useAudioPlayer();
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(undefined); // undefined=loading, null=none
  const [tracks, setTracks] = useState([]);
  const [drawer, setDrawer] = useState(null); // clicked locked track

  useEffect(() => {
    axios.get(`${API}/home/featured-track`).then((r) => setFeatured(r.data)).catch(() => setFeatured(null));
    axios.get(`${API}/music/tracks`).then((r) => setTracks(r.data || [])).catch(() => {});
  }, []);

  if (featured === undefined) return null;
  if (!featured) return null;

  const teaser = featured.description
    || (featured.featuring ? `feat. ${featured.featuring}` : '')
    || fmtList(featured.genre)
    || '';

  const grid = tracks.filter((t) => t.id !== featured.id);

  return (
    <section data-testid="home-music-section" className="relative py-20 md:py-28 overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
      <div aria-hidden className="pointer-events-none absolute inset-0" style={{ background: 'radial-gradient(ellipse at 20% 0%, color-mix(in srgb, var(--accent-primary) 12%, transparent), transparent 55%)' }} />
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.035]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27140%27 height=%27140%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.85%27 numOctaves=%272%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")' }} />
      <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
        <span className="text-overline">{t('home.music.overline')}</span>

        {/* Featured Track */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-8 flex flex-col md:flex-row gap-8 md:gap-14 items-center"
          data-testid="home-featured-track"
        >
          <div className="relative group w-64 h-64 md:w-80 md:h-80 flex-shrink-0">
            <div className="absolute -inset-1 rounded-3xl opacity-50 blur-xl" style={{ background: 'var(--accent-primary)' }} />
            {featured.cover
              ? <TileImage src={featured.cover} alt={featured.title} testid="home-featured-cover" extraTransition="transform 500ms"
                  className="gold-tier1 relative w-full h-full object-cover rounded-3xl transition-transform duration-500 group-hover:scale-105" />
              : <div className="gold-tier1 relative w-full h-full rounded-3xl" style={{ background: 'var(--bg-subtle)' }} />}
          </div>

          <div className="flex-1 text-center md:text-left">
            <h2 className="text-h1" style={{ color: 'var(--text-primary)' }} data-testid="home-featured-title">
              {featured.title}
            </h2>
            {teaser && (
              <p className="mt-3 text-body-lg" style={{ color: 'var(--text-secondary)' }} data-testid="home-featured-teaser">
                {teaser}
              </p>
            )}

            <div className="mt-8 flex flex-wrap gap-4 justify-center md:justify-start items-center">
              <SButton
                variant="primary"
                data-testid="home-featured-cta"
                onClick={() => { if (!everSubscribed) trackClick('subscribe_cta', { source: 'home_music_featured' }); navigate(everSubscribed ? '/music' : '/pricing'); }}
              >
                {everSubscribed ? t('home.music.listen_now') : t('home.music.listen_platform')}
              </SButton>
              <TipWidget
                sourceType="homepage"
                sourceId={featured.id}
                compact
                trigger={({ onClick }) => (
                  <SButton variant="secondary" onClick={onClick} data-testid="home-featured-tip">
                    {t('home.music.tip')} 🎵
                  </SButton>
                )}
              />
            </div>
          </div>
        </motion.div>

        {/* Track Catalog Grid - non-subscribers only. Per-tile lock respects each
            track's actual locked/visibility (public tracks are guest-playable). */}
        {!everSubscribed && grid.length > 0 && (
          <div className="mt-16 md:mt-20" data-testid="home-track-catalog">
            <div className="flex gap-4 md:gap-6 overflow-x-auto pb-3 snap-x snap-mandatory -mx-6 px-6 md:mx-0 md:px-0" style={{ scrollbarWidth: 'thin' }} data-testid="home-track-catalog-row">
              {grid.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => (t.locked ? setDrawer(t) : playTrack(t, grid.filter((x) => !x.locked)))}
                  data-testid={`home-track-tile-${t.id}`}
                  className="text-left group focus:outline-none flex-shrink-0 snap-start w-40 md:w-52 transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="relative rounded-2xl overflow-hidden" style={{ aspectRatio: '1 / 1', background: 'var(--bg-subtle)' }}>
                    {t.cover
                      ? <TileImage src={t.cover} alt={t.title} extraTransition="transform 500ms" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      : <div className="w-full h-full" />}
                    <div className="absolute inset-0 flex items-center justify-center transition-colors" style={{ background: 'rgba(10,15,26,0.35)' }}>
                      <span className="flex items-center justify-center w-11 h-11 rounded-full" style={{ background: 'rgba(255,255,255,0.9)' }}>
                        {t.locked
                          ? <Lock size={18} style={{ color: 'var(--accent-primary)' }} />
                          : <Play size={18} style={{ color: 'var(--accent-primary)' }} fill="var(--accent-primary)" />}
                      </span>
                    </div>
                  </div>
                  <p className="mt-3 text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{t.title}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                    {[t.duration, fmtList(t.genre)].filter(Boolean).join(' · ')}
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Inline subscribe drawer - slides up on locked tile click */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setDrawer(null)}
              className="fixed inset-0 z-[10000]"
              style={{ background: 'rgba(10,15,26,0.5)' }}
            />
            <motion.div
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              className="fixed bottom-0 left-0 right-0 z-[10001] px-6 pt-6 pb-8"
              style={{ background: 'var(--bg-surface)', borderTop: '1px solid var(--border-color)', borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
              data-testid="home-subscribe-drawer"
            >
              <div className="max-w-md mx-auto relative">
                <button onClick={() => setDrawer(null)} data-testid="home-drawer-close"
                  className="absolute -top-1 right-0 p-1" style={{ color: 'var(--text-secondary)' }}>
                  <X size={18} />
                </button>
                <div className="flex items-center gap-2 mb-2">
                  <Lock size={18} style={{ color: 'var(--accent-primary)' }} />
                  <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
                    {t('home.music.drawer_title')}
                  </h3>
                </div>
                <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
                  <CopyWithCoinPrice template={t('home.music.drawer_sub', { price: COIN_PRICE_SLOT })} coins={joinCoins} size="sm" />
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <SButton variant="primary" data-testid="home-drawer-subscribe" onClick={() => { trackClick('subscribe_cta', { source: 'home_music_drawer' }); navigate('/pricing'); }} className="w-full sm:w-auto">
                    <Play size={15} /> {t('home.music.drawer_subscribe')}
                  </SButton>
                  <button onClick={() => navigate('/login')} data-testid="home-drawer-login"
                    className="text-sm font-semibold" style={{ color: 'var(--accent-primary)' }}>
                    {t('home.music.drawer_login')}
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HomeMusic;
